/**
 * SendLeaseModal — One-tap "Send Lease for Signature" for a specific lease record.
 *
 * Flow:
 *   1. Fetches /esign/config, auto-picks the first template whose title contains "lease"
 *   2. Fetches /esign/templates/{id} to get signer_roles + custom_fields
 *   3. Fetches /esign/lease-prefill to load the landlord for this address
 *   4. Prefills signer roles (Tenant + Landlord) from the lease + property_configs
 *   5. Prefills any known custom fields (rent, pets, parking, utilities, unit, address, end date)
 *   6. Renders an editable form for every field so the user can override per-lease values
 *   7. POST /esign/send-template
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Sparkle, PaperPlaneTilt, Warning, Info, User, House,
} from "@phosphor-icons/react";

// Heuristics to auto-fill known custom-field names from a lease record.
// Any field name that includes one of the keywords (case-insensitive) is
// prefilled with the mapped lease value; user can still override.
const AUTO_MAP = [
  { keys: ["rent", "monthly_rent", "amount"],
    getter: (l) => l.proposed_rent_cents ? `$${(l.proposed_rent_cents/100).toLocaleString()}`
                                          : (l.current_rent_cents ? `$${(l.current_rent_cents/100).toLocaleString()}` : "") },
  { keys: ["tenant", "resident", "signer1"], getter: (l) => l.tenant_name || "" },
  { keys: ["address", "premises", "property"], getter: (l) => l.property_address || "" },
  { keys: ["unit", "apartment", "apt"], getter: (l) => l.unit || "" },
  { keys: ["end", "expiration", "term_end"], getter: (l) => l.lease_end || "" },
  { keys: ["start", "commence", "begin"], getter: (l) => l.lease_start || "" },
];

function autoFillCustomFields(lease, fields) {
  const out = {};
  for (const f of fields || []) {
    const key = (f.name || f.api_id || "").toLowerCase();
    if (!key) continue;
    for (const rule of AUTO_MAP) {
      if (rule.keys.some(k => key.includes(k))) {
        const v = rule.getter(lease);
        if (v) { out[f.name] = v; break; }
      }
    }
  }
  return out;
}

export default function SendLeaseModal({ lease, onClose, onSent }) {
  const [config, setConfig]     = useState(null);
  const [tmpl, setTmpl]         = useState(null);  // full template details
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [subject, setSubject]   = useState(
    `Lease renewal — ${lease.property_address}${lease.unit ? ` · ${lease.unit}` : ""}`
  );
  const [message, setMessage]   = useState(
    "Please review and sign your lease renewal. Reach out with any questions."
  );
  const [signers, setSigners]   = useState({});   // { role: {name, email_address} }
  const [customFields, setCF]   = useState({});   // { fieldName: value }
  const [prefillFound, setPF]   = useState(false);

  // Step 1: load config → find lease template
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/esign/config");
        setConfig(data);
        if (!data.configured || !data.templates?.length) {
          setLoading(false);
          return;
        }
        const kw = "lease";
        const picked = data.templates.find(t => (t.title || "").toLowerCase().includes(kw)) || data.templates[0];
        await loadTemplate(picked.template_id);
      } catch (e) {
        toast.error("Failed to load Dropbox Sign config");
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const loadTemplate = useCallback(async (tid) => {
    try {
      const [tRes, pRes] = await Promise.all([
        api.get(`/esign/templates/${tid}`),
        api.get(`/esign/lease-prefill`, { params: { property_address: lease.property_address, unit: lease.unit || "" } }).catch(() => ({ data: { landlord: {} } })),
      ]);
      setTmpl(tRes.data);
      setPF(!!pRes.data.found);

      // Prefill signer roles
      const nextSigners = {};
      for (const role of (tRes.data.signer_roles || [])) {
        const roleName = role.name;
        const low = (roleName || "").toLowerCase();
        if (low.includes("tenant") || low.includes("resident") || low.includes("renter")) {
          nextSigners[roleName] = { name: lease.tenant_name || "", email_address: lease.tenant_email || "" };
        } else if (low.includes("landlord") || low.includes("owner") || low.includes("lessor") || low.includes("manager")) {
          nextSigners[roleName] = pRes.data.landlord || { name: "", email_address: "" };
        } else {
          nextSigners[roleName] = { name: "", email_address: "" };
        }
      }
      setSigners(nextSigners);

      // Prefill custom fields with what we know
      setCF(autoFillCustomFields(lease, tRes.data.custom_fields));
    } catch (e) {
      toast.error(`Template load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [lease]);

  const templates = config?.templates || [];
  const canSend = useMemo(() => {
    if (!tmpl || sending) return false;
    // Every role must have name + email
    for (const role of (tmpl.signer_roles || [])) {
      const s = signers[role.name];
      if (!s || !s.name?.trim() || !s.email_address?.trim()) return false;
    }
    // Every required custom field must be filled
    for (const f of (tmpl.custom_fields || [])) {
      if (f.required && !String(customFields[f.name] || "").trim()) return false;
    }
    return true;
  }, [tmpl, signers, customFields, sending]);

  const updateSigner = (role, key, val) =>
    setSigners(s => ({ ...s, [role]: { ...(s[role] || {}), [key]: val } }));
  const updateCF = (name, val) =>
    setCF(cf => ({ ...cf, [name]: val }));

  const submit = async () => {
    if (!canSend || !tmpl) return;
    setSending(true);
    try {
      const signerList = (tmpl.signer_roles || []).map(r => ({
        role: r.name,
        name: signers[r.name]?.name || "",
        email_address: signers[r.name]?.email_address || "",
      }));
      const fd = new FormData();
      fd.append("parent_type", "lease");
      fd.append("parent_id",   String(lease.id));
      fd.append("template_id", tmpl.template_id);
      fd.append("subject",     subject);
      fd.append("message",     message);
      fd.append("title",       subject);
      fd.append("signers_json", JSON.stringify(signerList));
      fd.append("custom_fields_json", JSON.stringify(customFields));
      await api.post("/esign/send-template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Lease sent for signature");
      // Mark stage as offer_sent for continuity with the AI-draft flow
      try { await api.patch(`/lease-renewals/${lease.id}`, { stage: "offer_sent" }); } catch (_) {}
      onSent?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-3" data-testid="send-lease-modal">
      <div className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-gradient-to-r from-indigo-600 to-violet-700 text-white flex items-start justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> Send Lease · Dropbox Sign
            </div>
            <div className="font-black uppercase tracking-widest">{lease.tenant_name}</div>
            <div className="text-[10px] opacity-80">{lease.property_address}{lease.unit ? ` · ${lease.unit}` : ""}</div>
          </div>
          <button onClick={onClose} data-testid="send-lease-close" className="p-1 hover:bg-black/20">
            <X size={18}/>
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Loading template…</div>
        ) : !config?.configured ? (
          <div className="p-4 space-y-3">
            <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-3 flex gap-2">
              <Warning size={22} className="text-amber-700 shrink-0"/>
              <div className="text-sm text-amber-900">
                <div className="font-black">Dropbox Sign not configured</div>
                Add <code className="bg-amber-100 px-1 rounded">DROPBOX_SIGN_API_KEY</code> to backend/.env.
                Get your key at <a className="underline font-bold" href="https://app.hellosign.com/home/myAccount#api" target="_blank" rel="noreferrer">app.hellosign.com → API</a>.
              </div>
            </div>
          </div>
        ) : !templates.length ? (
          <div className="p-4 text-sm">
            No templates found in your Dropbox Sign account.{" "}
            <a href="https://app.hellosign.com/home/manageTemplates" target="_blank" rel="noreferrer" className="underline font-bold text-indigo-600">
              Create your lease template →
            </a>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {/* Template picker (defaulted, but overridable) */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Lease Template</label>
              <select
                value={tmpl?.template_id || ""}
                onChange={(e) => { setLoading(true); loadTemplate(e.target.value); }}
                className="mt-1 w-full h-10 border-2 border-slate-300 px-2 text-sm bg-white"
                data-testid="send-lease-template">
                {templates.map(t => (
                  <option key={t.template_id} value={t.template_id}>{t.title}</option>
                ))}
              </select>
              {config.test_mode && (
                <div className="mt-1 text-[10px] text-amber-700 font-bold flex items-center gap-1">
                  <Info size={10}/> Test mode — not legally binding. Flip <code className="bg-amber-100 px-1">DROPBOX_SIGN_TEST_MODE=false</code> to go live.
                </div>
              )}
            </div>

            {/* Signers */}
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Signers</div>
              <div className="space-y-2">
                {(tmpl?.signer_roles || []).map((r) => {
                  const isLandlordRole = /(landlord|owner|lessor|manager)/i.test(r.name);
                  return (
                    <div key={r.name} className="rounded-lg border-2 border-slate-200 p-2 bg-slate-50">
                      <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
                        {isLandlordRole ? <House size={11} weight="fill"/> : <User size={11} weight="fill"/>}
                        {r.name}
                        {isLandlordRole && prefillFound && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[9px] font-black">Auto-filled</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        <input value={signers[r.name]?.name || ""} onChange={e => updateSigner(r.name, "name", e.target.value)}
                          placeholder="Full name"
                          className="h-9 px-2 border-2 border-slate-200 text-sm bg-white"
                          data-testid={`send-lease-name-${r.name}`}/>
                        <input value={signers[r.name]?.email_address || ""} onChange={e => updateSigner(r.name, "email_address", e.target.value)}
                          placeholder="email@example.com" type="email"
                          className="h-9 px-2 border-2 border-slate-200 text-sm bg-white"
                          data-testid={`send-lease-email-${r.name}`}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Custom fields — the money shot: per-lease overrides */}
            {tmpl?.custom_fields?.length > 0 && (
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                  Fill-in Fields · <span className="text-slate-400 font-normal normal-case tracking-normal">edit anything per this lease</span>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {tmpl.custom_fields.map(f => {
                    const val = customFields[f.name] ?? "";
                    const isCheckbox = f.type === "checkbox";
                    const isMultiline = /note|clause|term|address/i.test(f.name) && !isCheckbox;
                    return (
                      <div key={f.name} className={isMultiline ? "col-span-2" : ""}>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center gap-1">
                          {f.name}
                          {f.required && <span className="text-red-500">*</span>}
                          {val && !isCheckbox && <span className="ml-auto text-[9px] font-normal text-emerald-600">✓ prefilled</span>}
                        </label>
                        {isCheckbox ? (
                          <div className="mt-1 h-9 flex items-center px-2 bg-slate-50 border-2 border-slate-200 rounded">
                            <input type="checkbox" checked={val === "true" || val === true}
                              onChange={(e) => updateCF(f.name, e.target.checked ? "true" : "false")}
                              data-testid={`send-lease-cf-${f.name}`}/>
                            <span className="ml-2 text-xs text-slate-500">Yes / checked</span>
                          </div>
                        ) : isMultiline ? (
                          <textarea value={val} onChange={e => updateCF(f.name, e.target.value)}
                            rows={2}
                            className="mt-1 w-full p-1.5 border-2 border-slate-200 text-sm bg-white"
                            data-testid={`send-lease-cf-${f.name}`}/>
                        ) : (
                          <input value={val} onChange={e => updateCF(f.name, e.target.value)}
                            className="mt-1 w-full h-9 px-2 border-2 border-slate-200 text-sm bg-white"
                            data-testid={`send-lease-cf-${f.name}`}/>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1.5 text-[10px] text-slate-500">
                  Common fields for lease variations: <b>Rent, Pets, Parking, Utilities</b>. Add these as
                  Merge Fields on your Dropbox Sign template to have them auto-appear here.
                </div>
              </div>
            )}

            {/* Subject / Message */}
            <div className="grid grid-cols-1 gap-2">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Email Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)}
                  className="mt-1 w-full h-9 px-2 border-2 border-slate-200 text-sm bg-white"
                  data-testid="send-lease-subject"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Email Message</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
                  className="mt-1 w-full p-1.5 border-2 border-slate-200 text-sm bg-white"
                  data-testid="send-lease-message"/>
              </div>
            </div>

            <button onClick={submit} disabled={!canSend}
              className="w-full h-12 bg-gradient-to-r from-indigo-600 to-violet-700 text-white font-black uppercase tracking-widest text-sm disabled:opacity-40 shadow-[3px_3px_0_#0f172a] inline-flex items-center justify-center gap-2"
              data-testid="send-lease-submit">
              <PaperPlaneTilt weight="fill" size={16}/>
              {sending ? "Sending…" : "Send for Signature"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
